import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircleOutline as CopiedIcon,
  ContentCopy as CopyIcon,
  East as RouteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getPairById, updatePairNote } from '@/services/api';
import ExchangeLogo, { resolveExchangeLogoSrc } from '@/components/common/ExchangeLogo';
import { useI18n } from '@/contexts/I18nContext';
import type { ArbitragePair, UserTransferDetail } from '@/types';

interface PairCardProps {
  pair: ArbitragePair;
  onUpdate?: () => void;
}

interface ExchangeLabelProps {
  id: string;
  name?: string | null;
  logoUrl?: string | null;
  allowWrap?: boolean;
}

interface WatermarkLogoProps extends ExchangeLabelProps {
  side: 'buy' | 'sell';
}

function ExchangeLabel({ id, name, logoUrl, allowWrap = false }: ExchangeLabelProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        flexShrink: 1,
      }}
    >
      <ExchangeLogo src={logoUrl} name={name} id={id} size={22} />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={
          allowWrap
            ? {
                minWidth: 0,
                lineHeight: 1.15,
                overflowWrap: 'anywhere',
                wordBreak: 'normal',
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
              }
            : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
        }
      >
        {name || id}
      </Typography>
    </Stack>
  );
}

function getExchangeInitials(name?: string | null, id?: string) {
  const source = (name || id || '').trim();
  if (!source) return '?';
  return source
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function WatermarkLogo({ id, name, logoUrl, side }: WatermarkLogoProps) {
  const resolvedLogoUrl = resolveExchangeLogoSrc(logoUrl, id);
  const commonSx = {
    position: 'absolute',
    width: { xs: 138, sm: 158 },
    height: { xs: 138, sm: 158 },
    opacity: 0.2,
    filter: 'grayscale(1) saturate(0.55) contrast(1.08)',
    pointerEvents: 'none',
  } as const;

  const positionSx = side === 'buy'
    ? { left: { xs: 18, sm: 28 }, bottom: { xs: -42, sm: -50 } }
    : { right: { xs: 12, sm: 22 }, top: { xs: -44, sm: -54 } };

  if (resolvedLogoUrl) {
    return (
      <Box
        component="img"
        src={resolvedLogoUrl}
        alt=""
        aria-hidden="true"
        sx={{
          ...commonSx,
          ...positionSx,
          objectFit: 'contain',
        }}
      />
    );
  }

  return (
    <Box
      aria-hidden="true"
      sx={{
        ...commonSx,
        ...positionSx,
        display: 'grid',
        placeItems: 'center',
        borderRadius: '50%',
        border: '2px solid currentColor',
        color: 'rgba(244, 255, 249, 0.88)',
        fontSize: { xs: 34, sm: 40 },
        fontWeight: 900,
      }}
    >
      {getExchangeInitials(name, id)}
    </Box>
  );
}

const clampPercent = (value: number) => Math.max(0, Math.min(100, Math.round(value)));
const SPREAD_DRIFT_INTERVAL_MS = 10 * 60 * 1000;
const SPREAD_DRIFT_RANGE = 0.1;
const PAIR_CARD_RADIUS = '8px';

function seededNoise(seed: string) {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  hash += hash << 13;
  hash ^= hash >>> 7;
  hash += hash << 3;
  hash ^= hash >>> 17;
  hash += hash << 5;

  return ((hash >>> 0) / 4294967295) * 2 - 1;
}

function getVisualSpreadPercent(pairId: number, spreadPercent: number | null, now = Date.now()) {
  if (spreadPercent === null) return null;

  const bucket = Math.floor(now / SPREAD_DRIFT_INTERVAL_MS);
  const rawNoise = seededNoise(`${pairId}:${bucket}`);
  const easedNoise = Math.sin(rawNoise * Math.PI * 0.5);
  const drifted = spreadPercent * (1 + easedNoise * SPREAD_DRIFT_RANGE);
  const precision = Math.abs(spreadPercent) < 1 ? 2 : 1;

  return Number(drifted.toFixed(precision));
}

function formatSpreadPercent(value: number | null, fallback: string) {
  if (value === null) return fallback;
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: value < 1 ? 2 : 1,
  })}%`;
}

export default function PairCard({ pair, onUpdate }: PairCardProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [noteText, setNoteText] = useState(pair.user_note || '');
  const [copiedRow, setCopiedRow] = useState<{ key: string; message: string } | null>(null);
  const [driftTime, setDriftTime] = useState(() => Date.now());
  const copiedTimerRef = useRef<number | null>(null);

  const { data: pairDetails } = useQuery({
    queryKey: ['pair', pair.id],
    queryFn: () => getPairById(pair.id),
    enabled: expanded && !pair.is_blurred,
  });

  const noteMutation = useMutation({
    mutationFn: (note: string) => updatePairNote(pair.id, note),
    onSuccess: () => {
      setEditingNote(false);
      onUpdate?.();
    },
  });

  useEffect(() => {
    const syncVisualSpread = () => {
      setDriftTime(Date.now());
    };
    const now = Date.now();
    const nextBucketDelay = SPREAD_DRIFT_INTERVAL_MS - (now % SPREAD_DRIFT_INTERVAL_MS);
    let intervalId: number | null = null;
    const timeoutId = window.setTimeout(() => {
      syncVisualSpread();
      intervalId = window.setInterval(syncVisualSpread, SPREAD_DRIFT_INTERVAL_MS);
    }, nextBucketDelay);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      if (copiedTimerRef.current) {
        window.clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const handleCopy = async (text: string, label: string, rowKey: string) => {
    await navigator.clipboard?.writeText(text);
    setCopiedRow({ key: rowKey, message: t('common.copied', { item: label }) });

    if (copiedTimerRef.current) {
      window.clearTimeout(copiedTimerRef.current);
    }

    copiedTimerRef.current = window.setTimeout(() => {
      setCopiedRow((current) => (current?.key === rowKey ? null : current));
    }, 1800);
  };

  const renderCopiedToast = (rowKey: string) =>
    copiedRow?.key === rowKey ? (
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          py: 0.55,
          borderRadius: 999,
          color: 'primary.main',
          bgcolor: 'rgba(8, 20, 15, 0.92)',
          border: '1px solid rgba(118, 244, 184, 0.34)',
          boxShadow: '0 10px 28px rgba(0, 0, 0, 0.32)',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      >
        <CopiedIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 900 }}>
          {copiedRow.message}
        </Typography>
      </Box>
    ) : null;

  const handleLogCircle = () => {
    navigate('/calculator', {
      state: {
        prefillCircle: {
          pair_id: pair.id,
          coin_from: pair.coin_from,
          coin_to: pair.coin_to,
          exchange_buy: pair.exchange_buy,
          exchange_sell: pair.exchange_sell,
          pair_type: pair.pair_type,
        },
      },
    });
  };

  const handleToggleExpanded = () => {
    onUpdate?.();
    setExpanded((value) => !value);
  };

  const coinLabel = pair.pair_type === 'single' ? pair.coin_from : `${pair.coin_from} -> ${pair.coin_to}`;
  const effectiveSpread = pair.effective_spread_percent ?? pair.spread_percent;
  const visualSpreadPercent = getVisualSpreadPercent(pair.id, effectiveSpread, driftTime);
  const isBlurred = Boolean(pair.is_blurred);
  const spreadValue = formatSpreadPercent(visualSpreadPercent, t('pair.na'));
  const isStrongSpread = (visualSpreadPercent ?? 0) >= 2;
  const isNegativeSpread = (visualSpreadPercent ?? 0) < 0;
  const hasSafetyPercent = pair.safety_percent !== null && pair.safety_percent !== undefined;
  const safetyPercent = hasSafetyPercent ? clampPercent(pair.safety_percent!) : null;
  const ribbonColor = pair.ribbon_color || (pair.is_active ? '#54E38D' : '#FF6B7A');
  const privateHiddenSx = isBlurred
    ? {
        filter: 'blur(5px)',
        opacity: 0.58,
        userSelect: 'none',
      }
    : undefined;

  return (
    <>
      <Box sx={{ position: 'relative', borderRadius: PAIR_CARD_RADIUS }}>
        <Card
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: PAIR_CARD_RADIUS,
            transition:
              'transform 220ms cubic-bezier(0.2, 0, 0, 1), border-color 220ms ease, background-color 220ms ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              borderColor: isStrongSpread ? ribbonColor : 'rgba(226, 255, 238, 0.22)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 12,
              bgcolor: ribbonColor,
              zIndex: 2,
            },
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `
                  radial-gradient(circle at 24% 82%, ${ribbonColor}24 0, transparent 36%),
                  radial-gradient(circle at 84% 18%, rgba(244, 255, 249, 0.15) 0, transparent 32%)
                `,
              },
            }}
          >
            <WatermarkLogo
              side="buy"
              id={pair.exchange_buy}
              name={pair.exchange_buy_name}
              logoUrl={pair.exchange_buy_logo_url}
            />
            <WatermarkLogo
              side="sell"
              id={pair.exchange_sell}
              name={pair.exchange_sell_name}
              logoUrl={pair.exchange_sell_logo_url}
            />
          </Box>
          <CardContent
            sx={{
              position: 'relative',
              zIndex: 1,
              p: 2,
              pl: 3.25,
              transform: 'translateZ(0)',
              '&:last-child': { pb: 2 },
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      ...privateHiddenSx,
                    }}
                  >
                    {coinLabel}
                  </Typography>
                  {pair.access_level === 'private' && (
                    <Tooltip title={t('pair.privateRoute')}>
                      <LockIcon color="secondary" sx={{ fontSize: 18, flexShrink: 0, ...privateHiddenSx }} />
                    </Tooltip>
                  )}
                </Stack>
              </Box>

              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Chip
                  label={spreadValue}
                  color={isNegativeSpread ? 'error' : isStrongSpread ? 'success' : 'default'}
                  variant={isStrongSpread || isNegativeSpread ? 'filled' : 'outlined'}
                  sx={{ minWidth: 68 }}
                />
                <Tooltip title={expanded ? t('pair.closeDetails') : t('pair.expandDetails')}>
                  <IconButton
                    size="small"
                    onClick={handleToggleExpanded}
                    disabled={isBlurred}
                    sx={privateHiddenSx}
                  >
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              sx={{
                mt: 0.85,
                minWidth: 0,
                maxWidth: '100%',
                columnGap: 0.75,
                rowGap: 0.45,
                flexWrap: 'wrap',
                ...privateHiddenSx,
              }}
            >
              <ExchangeLabel
                id={pair.exchange_buy}
                name={pair.exchange_buy_name}
                logoUrl={pair.exchange_buy_logo_url}
                allowWrap
              />
              <RouteIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              <ExchangeLabel
                id={pair.exchange_sell}
                name={pair.exchange_sell_name}
                logoUrl={pair.exchange_sell_logo_url}
                allowWrap
              />
            </Stack>

            {safetyPercent !== null && (
              <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                    {t('pair.safetySignal')}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    overflow: 'hidden',
                    bgcolor: 'rgba(244, 255, 249, 0.08)',
                  }}
                >
                  <Box
                    sx={{
                      width: `${safetyPercent}%`,
                      height: '100%',
                      borderRadius: 999,
                      bgcolor: ribbonColor,
                      boxShadow: `0 0 18px ${ribbonColor}55`,
                    }}
                  />
                </Box>
              </Box>
            )}

            {pair.notes ? (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: 2,
                  border: '1px solid rgba(124, 199, 255, 0.18)',
                  bgcolor: 'rgba(124, 199, 255, 0.07)',
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <InfoIcon sx={{ mt: 0.1, fontSize: 17, color: 'info.main', flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary">
                    {pair.notes}
                  </Typography>
                </Stack>
              </Box>
            ) : null}

            <Collapse in={expanded}>
              <Divider sx={{ my: 2 }} />

              {pairDetails?.transfer_details && pairDetails.transfer_details.length > 0 ? (
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('pair.transferDetails')}
                  </Typography>
                  {pairDetails.transfer_details.map((detail: UserTransferDetail) => (
                    <Box
                      key={detail.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'rgba(244, 255, 249, 0.045)',
                        border: '1px solid rgba(226, 255, 238, 0.10)',
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                          {detail.exchange_name || pair.exchange_sell_name || pair.exchange_sell}
                        </Typography>
                      </Stack>
                      {detail.deposit_address && (
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          spacing={1}
                          sx={{ position: 'relative', pr: 5 }}
                        >
                          <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                            <Box component="span" color="text.secondary">
                              {t('pair.address')}:
                            </Box>{' '}
                            {detail.deposit_address}
                          </Typography>
                          <Tooltip title={t('pair.copyAddress')}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopy(detail.deposit_address!, t('pair.address'), `${detail.id}-address`)
                              }
                              sx={{ width: 28, height: 28, p: 0.5 }}
                            >
                              <CopyIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {renderCopiedToast(`${detail.id}-address`)}
                        </Stack>
                      )}
                      {detail.memo && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ position: 'relative', mt: 0.75, pr: 5 }}
                        >
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            <Box component="span" color="text.secondary">
                              {t('pair.memo')}:
                            </Box>{' '}
                            {detail.memo}
                          </Typography>
                          <Tooltip title={t('pair.copyMemo')}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(detail.memo!, t('pair.memo'), `${detail.id}-memo`)}
                              sx={{ width: 28, height: 28, p: 0.5 }}
                            >
                              <CopyIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {renderCopiedToast(`${detail.id}-memo`)}
                        </Stack>
                      )}
                      {detail.network && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {t('pair.network')}: {detail.network}
                        </Typography>
                      )}
                      {detail.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {t('pair.notes')}: {detail.notes}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('pair.transferDetailsMissing')}
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('pair.personalNote')}
                  </Typography>
                  <Tooltip title={editingNote ? t('pair.closeNoteEditor') : t('pair.editNote')}>
                    <IconButton size="small" onClick={() => setEditingNote((value) => !value)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {editingNote ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      value={noteText}
                      onChange={(event) => setNoteText(event.target.value)}
                      placeholder={t('pair.addPersonalNote')}
                    />
                    <Button
                      variant="contained"
                      onClick={() => noteMutation.mutate(noteText)}
                      disabled={noteMutation.isPending}
                    >
                      {t('common.save')}
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" color={pair.user_note ? 'text.primary' : 'text.secondary'}>
                    {pair.user_note || t('pair.noNoteAdded')}
                  </Typography>
                )}
              </Box>

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleLogCircle}>
                  {t('pair.logRound')}
                </Button>
              </Stack>
            </Collapse>
          </CardContent>
        </Card>

        {isBlurred && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              borderRadius: PAIR_CARD_RADIUS,
              backgroundColor: 'rgba(0, 0, 0, 0.18)',
              border: '1px solid rgba(255, 209, 102, 0.46)',
              boxShadow: 'inset 0 0 28px rgba(0, 0, 0, 0.28)',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '1px',
                borderRadius: '7px',
                backgroundImage:
                  'repeating-linear-gradient(135deg, rgba(255, 209, 102, 0.94) 0, rgba(255, 209, 102, 0.94) 22px, rgba(0, 0, 0, 0.46) 22px, rgba(0, 0, 0, 0.46) 44px)',
                backgroundPosition: '0 0',
                pointerEvents: 'none',
              },
              '& .MuiChip-root': {
                position: 'relative',
                zIndex: 1,
              },
            }}
          >
            <Chip icon={<LockIcon />} label={t('common.private')} color="secondary" />
          </Box>
        )}
      </Box>

    </>
  );
}
